import {Howl} from 'howler';

// In-memory cache for storing Howl instances
const audioCache: Record<string, Howl> = {};

// Current sound reference
let currentSound: Howl | null = null;
let currentSoundUrl: string | null = null;

/**
 * Loads a sound file from the given URL.
 * @param url - The URL of the sound file.
 * @returns A promise that resolves when the sound is loaded.
 */
const loadSound = (url: string): Promise<void> => {
  if (!url) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    try {
      // Check if the sound is already cached
      if (audioCache[url]) {
        currentSound = audioCache[url];
        currentSoundUrl = url;
        resolve();
        return;
      }

      // Create a new Howl instance
      const sound = new Howl({
        src: [url],
        html5: true, // Better for streaming and large files
        preload: true,
        onload: () => {
          audioCache[url] = sound;
          currentSound = sound;
          currentSoundUrl = url;
          resolve();
        },
        onloaderror: (_id: number, error: unknown) => {
          reject(error);
        },
      });
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Plays the loaded sound.
 * @param onEndCallback - Optional callback to be called when the sound playback ends.
 */
const play = async (onEndCallback?: () => void): Promise<void> => {
  if (currentSound) {
    // If already playing, restart from the beginning
    if (currentSound.playing()) {
      currentSound.stop();
    }

    // Set up end callback
    currentSound.once('end', () => {
      onEndCallback?.();
    });

    // Set up error callback
    currentSound.once('playerror', () => {
      onEndCallback?.();
    });

    // Play the sound
    currentSound.play();
  } else {
    onEndCallback?.();
  }
};

/**
 * Pauses the playing sound.
 */
const pause = (): void => {
  if (currentSound) {
    currentSound.pause();
  }
};

/**
 * Stops the playing sound.
 */
const stop = (): void => {
  if (currentSound) {
    currentSound.stop();
  }
};

/**
 * Sets the volume of the sound.
 * @param volume - The volume level (0.0 to 1.0).
 */
const setVolume = (volume: number): void => {
  const normalizedVolume = Math.max(0, Math.min(1, volume));
  if (currentSound) {
    currentSound.volume(normalizedVolume);
  }
};

/**
 * Sets the pan of the sound.
 * @param pan - The pan level (-1.0 to 1.0).
 */
const setPan = (pan: number): void => {
  const normalizedPan = Math.max(-1, Math.min(1, pan));
  if (currentSound) {
    currentSound.stereo(normalizedPan);
  }
};

/**
 * Sets the number of loops for the sound.
 * @param loops - The number of times to loop (-1 for infinite looping).
 */
const setNumberOfLoops = (loops: number): void => {
  if (currentSound) {
    if (loops === -1) {
      currentSound.loop(true);
    } else if (loops > 0) {
      // Howler doesn't directly support a specific number of loops
      // We set loop to true and count manually
      let loopCount = 0;
      currentSound.loop(true);

      // Add a listener that counts loops and stops after reaching the limit
      const onEndFunc = () => {
        loopCount++;
        if (loopCount >= loops) {
          currentSound?.loop(false);
          currentSound?.off('end', onEndFunc);
        }
      };

      currentSound.on('end', onEndFunc);
    } else {
      currentSound.loop(false);
    }
  }
};

/**
 * Checks if the sound is currently playing.
 * @returns True if the sound is playing, otherwise false.
 */
const isPlaying = (): boolean => {
  return currentSound ? currentSound.playing() : false;
};

/**
 * Releases the sound from memory.
 */
const releaseSound = (): void => {
  if (currentSound) {
    currentSound.unload();
    if (currentSoundUrl) {
      delete audioCache[currentSoundUrl];
    }
    currentSound = null;
    currentSoundUrl = null;
  }
};

/**
 * Releases all sounds from memory and clears the cache.
 */
const releaseAllSounds = (): void => {
  // First release the current sound
  if (currentSound) {
    currentSound.unload();
    currentSound = null;
    currentSoundUrl = null;
  }

  // Then unload all cached sounds
  Object.values(audioCache).forEach(sound => {
    sound.unload();
  });

  // Clear the cache
  Object.keys(audioCache).forEach(key => {
    delete audioCache[key];
  });
};

/**
 * Reloads the sound with a new URL.
 * @param newUrl - The new URL of the sound file.
 * @returns A promise that resolves when the new sound is loaded.
 */
const reloadSound = (newUrl: string): Promise<void> => {
  // Only unload the current sound, keep the cache
  if (currentSound) {
    currentSound.unload();
    currentSound = null;
    currentSoundUrl = null;
  }

  return loadSound(newUrl);
};

// Export the sound service interface
export default {
  loadSound,
  play,
  pause,
  stop,
  setVolume,
  setPan,
  setNumberOfLoops,
  isPlaying,
  releaseSound,
  reloadSound,
  releaseAllSounds,
};
